/* eslint require-jsdoc: 'error', valid-jsdoc: ["error", { "requireReturn": false }] */
import ScatterDataset from '@fusioncharts/charts/src/dataset/scatter';
import { preDefStr, extend2, pluck, pluckNumber,
  ZEROSTRING, bindSelectionEvent, getMouseCoordinate, convertColor } from '@fusioncharts/core/src/lib';
import { getDepsByType, addDep } from '@fusioncharts/core/src/dependency-manager';
import selectScatterAnimation from './index.animation';

var COLOR_FFFFFF = 'FFFFFF',
  XY_STR = 'xy',
  altHGridColorStr = preDefStr.altHGridColorStr,
  altHGridAlphaStr = preDefStr.altHGridAlphaStr;

addDep({
  name: 'selectScatterAnimation',
  type: 'animationRule',
  extension: selectScatterAnimation
});
/**
 * Creates dataset class of Select scatter
 */
class SelectScatterDataset extends ScatterDataset {
  /**
   * Sets the type of the component
   * @return {string} type
   */
  getType () {
    return 'dataset';
  }
  /**
   * Sets the name of the component
   * @return {string} name
   */
  getName () {
    return 'selectScatter';
  }
  /**
   * Configure default settings for datasets
   * @param {Object} datasetJSON dataset json
   */
  configure (datasetJSON) {
    super.configure(datasetJSON);
    var dataSet = this,
      chart = dataSet.getFromEnv('chart'),
      chartConfig = chart.config,
      chartAttr = chart.getFromEnv('dataSource').chart,
      colorM = dataSet.getFromEnv('color-manager'),
      borderColor = pluck(chartAttr.selectbordercolor,
        colorM.getColor('canvasBorderColor')),
      borderAlpha = pluckNumber(chartAttr.selectborderalpha,
        colorM.getColor('canvasBorderAlpha'));

    chartConfig.selectBorderColor = // convertColor(borderColor, borderAlpha);
    {
      FCcolor: {
        color: borderColor,
        alpha: borderAlpha
      }
    };
    chartConfig.selectFillColor = convertColor(
      pluck(chartAttr.selectfillcolor,
        colorM.getColor(altHGridColorStr)),
      pluckNumber(chartAttr.selectfillalpha,
        colorM.getColor(altHGridAlphaStr)));

    chartConfig.selectionCancelButtonBorderColor = convertColor(pluck(
      chartAttr.selectioncancelbuttonbordercolor, borderColor),
    pluckNumber(chartAttr.selectioncancelbuttonborderalpha, borderAlpha));
    chartConfig.selectionCancelButtonFillColor = convertColor(pluck(
      chartAttr.selectioncancelbuttonfillcolor, COLOR_FFFFFF),
    pluckNumber(chartAttr.selectioncancelbuttonfillalpha, 100));
    chartConfig.connativeZoom = false;
    chartConfig.zoomType = XY_STR;

    if (chartAttr.submitdataasxml === ZEROSTRING && !chartAttr.formdataformat) {
      chartAttr.formdataformat = getDepsByType('transcoder').csv && 'csv';
    }
    // @ temp will work after transcoder fix
    // chartConfig.formDataFormat = pluck(chartAttr.formdataformat,
    //             global.dataFormats.XML);
    chartConfig.formDataFormat = pluck(chartAttr.formdataformat);
    chartConfig.formTarget = pluck(chartAttr.formtarget, '_self');
    chartConfig.formMethod = pluck(chartAttr.formmethod, 'POST');
    chartConfig.submitFormAsAjax = pluckNumber(chartAttr.submitformusingajax, 1);
  }
  /**
   * Draws plot graphics
   */
  draw () {
    super.draw();
    var dataSet = this,
      chart = dataSet.getFromEnv('chart'),
      container = chart.getFromEnv('chart-container'),
      xAxis = dataSet.getFromEnv('xAxis'),
      yAxis = dataSet.getFromEnv('yAxis');

    bindSelectionEvent(chart, {
      selectionStart: function (data) {
        var pos = getMouseCoordinate(container, data.originalEvent, chart), // eslint-disable-line good-practices/no-single-usage-variable
          eventArgs = extend2({ // eslint-disable-line good-practices/no-single-usage-variable
            selectionLeft: data.selectionLeft,
            selectionTop: data.selectionTop,
            selectionWidth: data.selectionWidth,
            selectionHeight: data.selectionHeight,
            startXValue: xAxis.getValue(data.selectionLeft, 1),
            startYValue: yAxis.getValue(data.selectionTop, 1)
          }, pos);
        /**
         * Raised when user starts to draw a selection box on a `selectScatter` chart.
         * @event FusionCharts#selectionStart
         *
         * @param {number} chartX - The x-coordinate of the mouse with respect to the chart.
         * @param {number} chartY - The y-coordinate of the mouse with respect to the chart.
         * @param {number} pageX - The x-coordinate of the mouse with respect to the page.
         * @param {number} pageY - The y-coordinate of the mouse with respect to the page.
         * @param {number} startXValue - The value on the canvas x-axis where the selection started.
         * @param {number} startYValue - The value on the canvas y-axis where the selection started.
         */
        data.chart.fireChartInstanceEvent('selectionStart', eventArgs);
      },
      selectionEnd: function (data) {
        var pos = getMouseCoordinate(container, data.originalEvent, chart), // eslint-disable-line good-practices/no-single-usage-variable
          eventArgs = extend2({ // eslint-disable-line good-practices/no-single-usage-variable
            selectionLeft: data.selectionLeft,
            selectionTop: data.selectionTop,
            selectionWidth: data.selectionWidth,
            selectionHeight: data.selectionHeight,
            startXValue: xAxis.getValue(data.selectionLeft, 1),
            startYValue: yAxis.getValue(data.selectionTop, 1),
            endXValue: xAxis.getValue(data.selectionLeft + data.selectionWidth, 1),
            endYValue: yAxis.getValue(data.selectionTop + data.selectionHeight, 1)
          }, pos);

        /**
         * Raised when user completes a selection box on a `selectScatter` chart.
         * @event FusionCharts#selectionEnd
         *
         * @param {number} chartX - The x-coordinate of the mouse with respect to the chart.
         * @param {number} chartY - The y-coordinate of the mouse with respect to the chart.
         * @param {number} pageX - The x-coordinate of the mouse with respect to the page.
         * @param {number} pageY - The y-coordinate of the mouse with respect to the page.
         * @param {number} startXValue - The value on the canvas x-axis where the selection started.
         * @param {number} startYValue - The value on the canvas y-axis where the selection started.
         * @param {number} endXValue - The value on the canvas x-axis where the selection ended.
         * @param {number} endYValue - The value on the canvas y-axis where the selection ended.
         * @param {number} selectionLeft - The x-coordinate from where selection started with
         * respect to the chart.
         * @param {number} selectionTop - The y-coordinate from where selection started with
         * respect to the chart.
         * @param {number} selectionWidth - The width of the selection in pixels.
         * @param {number} selectionHeight - The height of the selection box in pixels.
         */
        data.chart.fireChartInstanceEvent('selectionEnd', eventArgs);
        data.chart.createSelectionBox(data);
      }
    });
  }
}

export default SelectScatterDataset;
